import { Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { Config } from '@shared/configs/config';
import { formatApiUtcDateTime } from '@shared/helpers/date-format.helper';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { Task, TaskPriority } from '@shared/models/task.model';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { ETaskAction } from '@shared/modules/task-sidenav/enums/task-action.enum';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import * as moment from 'moment';
import { getTaskDeadlineLabel, getTaskDeadlineTone, getTaskTermRange } from '@shared/modules/list/helpers/task-deadline.helper';
import { Subscription } from 'rxjs';

@Component({
  selector: 'task-sidenav-task-meta-fields',
  templateUrl: './task-sidenav-task-meta-fields.component.html',
  styleUrls: ['./task-sidenav-task-meta-fields.component.scss']
})
export class TaskSidenavTaskMetaFieldsComponent implements OnDestroy {
  taskPriorities: TaskPriority[] = [4, 1, 2, 3];
  rangeStart = new FormControl<moment.Moment | null>(null);
  rangeEnd = new FormControl<moment.Moment | null>(null);
  prioritySaving = false;
  minutesSaving = false;
  deadlineSaving = false;
  private taskActionSub: Subscription;

  constructor(
    public service: TaskSidenavService,
    private taskService: TaskService,
    private listTaskService: ListTaskService,
    private snackBar: SnackBarService,
    private translate: TranslateService
  ) {
    this.taskActionSub = this.service.taskActionEmitter.subscribe((event) => {
      if (event.action === ETaskAction.ACTION_TASK_LOADED || event.action === ETaskAction.ACTION_UPDATE) this.syncRange();
    });
  }

  get currentTaskPriority(): TaskPriority {
    return this.service.form?.get(this.service.TASK_SIDENAV_FORM.priority)?.value ?? this.service.task?.priority ?? 4;
  }

  get deadlineLabel(): string {
    return getTaskDeadlineLabel(this.termStartValue, this.termEndValue, this.translate);
  }

  get deadlineTone(): 'today' | 'past' | 'normal' {
    return getTaskDeadlineTone(this.termEndValue);
  }

  /** Full range of dates, useful when the label is a text (e.g. today) or only a single date */
  get deadlineTooltip(): string {
    const range = getTaskTermRange(this.termStartValue, this.termEndValue);
    return range && range !== this.deadlineLabel ? range : '';
  }

  private get termStartValue(): string | null {
    return this.service.form?.get(this.service.TASK_SIDENAV_FORM.termStart)?.value;
  }

  private get termEndValue(): string | null {
    return this.service.form?.get(this.service.TASK_SIDENAV_FORM.termEnd)?.value;
  }

  priorityLabel(priority: TaskPriority): string {
    return ({ 1: 'sofort', 2: 'dringend', 3: 'wichtig', 4: 'none' } as const)[priority];
  }

  ngOnDestroy(): void {
    this.taskActionSub.unsubscribe();
  }

  onTaskPrioritySelected(priority: TaskPriority): void {
    if (this.prioritySaving || this.service.task?.isDeleted || this.service.loading || this.currentTaskPriority === priority) return;
    const control = this.service.form.get(this.service.TASK_SIDENAV_FORM.priority);
    if (this.service.isCreation) {
      control?.setValue(priority);
      return;
    }
    const task = this.service.task;
    if (!task?.id) return;
    this.prioritySaving = true;
    this.taskService.editPatchTask({ priority }, task.id).subscribe({
      next: (response: Task) => {
        this.prioritySaving = false;
        if (this.service.task?.id !== task.id) return;
        task.priority = response?.priority ?? priority;
        control?.setValue(task.priority, { emitEvent: false });
        this.updateSavedValue(this.service.TASK_SIDENAV_FORM.priority, task.priority);
        this.emitTaskUpdate(task);
        this.showSaved();
      },
      error: () => {
        this.prioritySaving = false;
        this.snackBar.error(this.translate.instant('MyWork.Card.savePriorityError'));
      }
    });
  }

  onEstimatedTimeBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value.trim();
    const estimatedTime = rawValue === '' ? null : Number(rawValue);
    const control = this.service.form.get(this.service.TASK_SIDENAV_FORM.estimatedTime);
    const previous = control?.value ?? null;
    if (estimatedTime !== null && (!Number.isInteger(estimatedTime) || estimatedTime < 0 || estimatedTime > 525600)) {
      input.value = String(previous ?? '');
      this.snackBar.error(this.translate.instant('MyWork.Card.invalidMinutes'));
      return;
    }
    if (this.minutesSaving || previous === estimatedTime) return;
    if (this.service.isCreation) {
      control?.setValue(estimatedTime);
      return;
    }
    const task = this.service.task;
    if (!task?.id || task.isDeleted || this.service.loading) {
      input.value = String(previous ?? '');
      return;
    }
    this.minutesSaving = true;
    this.taskService.editPatchTask({ estimatedTime }, task.id).subscribe({
      next: (response: Task) => {
        this.minutesSaving = false;
        if (this.service.task?.id !== task.id) return;
        task.estimatedTime = response?.estimatedTime ?? estimatedTime;
        control?.setValue(task.estimatedTime, { emitEvent: false });
        this.updateSavedValue(this.service.TASK_SIDENAV_FORM.estimatedTime, task.estimatedTime);
        this.emitTaskUpdate(task);
        this.showSaved();
      },
      error: () => {
        this.minutesSaving = false;
        input.value = String(previous ?? '');
        this.snackBar.error(this.translate.instant('MyWork.Card.saveMinutesError'));
      }
    });
  }

  onEstimatedTimeKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Escape') {
      input.value = String(this.service.form.get(this.service.TASK_SIDENAV_FORM.estimatedTime)?.value ?? '');
      input.blur();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      input.blur();
    }
  }

  openRangePicker(): void {
    this.syncRange();
  }

  onRangeEndChanged(event: MatDatepickerInputEvent<moment.Moment>): void {
    const start = this.rangeStart.value;
    const end = event.value;
    if (start?.isValid() && end?.isValid()) this.saveRange(start, end);
  }

  clearRange(event: Event): void {
    event.stopPropagation();
    this.saveRange(null, null);
  }

  private saveRange(start: moment.Moment | null, end: moment.Moment | null): void {
    if (this.deadlineSaving || this.service.task?.isDeleted || this.service.loading) return;
    if (this.service.isCreation) {
      this.service.rangeChanged({ start, end });
      this.syncRange();
      return;
    }
    const task = this.service.task;
    if (!task?.id) return;
    const termStart = start ? formatApiUtcDateTime(start) : null;
    const termEnd = end ? formatApiUtcDateTime(end, { endOfDay: true }) : null;
    if (termStart === task.termStart && termEnd === task.termEnd) return;
    this.deadlineSaving = true;
    this.taskService.editPatchTask({ termStart, termEnd }, task.id).subscribe({
      next: (response: Task) => {
        this.deadlineSaving = false;
        if (this.service.task?.id !== task.id) return;
        task.termStart = response?.termStart ?? termStart;
        task.termEnd = response?.termEnd ?? termEnd;
        task.termStartTheSameTermEnd = !!task.termStart && !!task.termEnd && moment(task.termStart).isSame(moment(task.termEnd), 'day');
        this.service.form.get(this.service.TASK_SIDENAV_FORM.termStart)?.setValue(task.termStart, { emitEvent: false });
        this.service.form.get(this.service.TASK_SIDENAV_FORM.termEnd)?.setValue(task.termEnd, { emitEvent: false });
        this.updateSavedValue(this.service.TASK_SIDENAV_FORM.termStart, task.termStart);
        this.updateSavedValue(this.service.TASK_SIDENAV_FORM.termEnd, task.termEnd);
        this.service.selectedDate = {
          start: task.termStart ? moment(task.termStart).format(Config.DATE_FORMAT_DOTS) : null,
          end: task.termEnd ? moment(task.termEnd).format(Config.DATE_FORMAT_DOTS) : null
        };
        this.syncRange();
        this.emitTaskUpdate(task);
        this.showSaved();
      },
      error: () => {
        this.deadlineSaving = false;
        this.syncRange();
        this.snackBar.error(this.translate.instant('MyWork.Card.saveDeadlineError'));
      }
    });
  }

  private syncRange(): void {
    const start = this.service.form?.get(this.service.TASK_SIDENAV_FORM.termStart)?.value;
    const end = this.service.form?.get(this.service.TASK_SIDENAV_FORM.termEnd)?.value;
    this.rangeStart.setValue(start ? moment(start) : null, { emitEvent: false });
    this.rangeEnd.setValue(end ? moment(end) : null, { emitEvent: false });
  }

  private updateSavedValue(field: string, value: unknown): void {
    if (this.service.savedFormValues) {
      this.service.savedFormValues = { ...this.service.savedFormValues, [field]: value };
    }
  }

  private emitTaskUpdate(task: Task): void {
    this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_TASK, data: { task } });
  }

  private showSaved(): void {
    this.snackBar.success(this.translate.instant('MyWork.Card.saveSuccess'));
  }
}
